import { useTheme } from "next-themes";
import { Toaster as Sonner, toast } from "sonner";

type ToasterProps = React.ComponentProps<typeof Sonner>;

/**
 * BeraStays Toaster
 * - Sits above every overlay (z-index 2147483000) so notifications stay visible
 *   even when a dimmed modal/drawer is open.
 * - Coral-branded surface (primary accent border + soft shadow), elegant rounded card.
 */
const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      position="top-center"
      offset="24px"
      // Above Radix overlays (z-50) and any custom dialog/drawer layer.
      style={{ zIndex: 2147483647, filter: "none" } as React.CSSProperties}
      toastOptions={{
        unstyled: false,
        classNames: {
          toast:
            "group toast pointer-events-auto " +
            "group-[.toaster]:!bg-background group-[.toaster]:!text-foreground " +
            "group-[.toaster]:border group-[.toaster]:border-primary/30 " +
            "group-[.toaster]:rounded-2xl group-[.toaster]:shadow-[0_20px_60px_-12px_rgba(0,0,0,0.35)] " +
            "group-[.toaster]:backdrop-blur-0 group-[.toaster]:px-4 group-[.toaster]:py-3 group-[.toaster]:!opacity-100",
          title: "group-[.toast]:text-foreground group-[.toast]:font-semibold",
          description: "group-[.toast]:text-muted-foreground",
          actionButton:
            "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground group-[.toast]:rounded-full",
          cancelButton:
            "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground group-[.toast]:rounded-full",
          success: "group-[.toaster]:!border-emerald-500/40",
          error: "group-[.toaster]:!border-destructive/50",
          warning: "group-[.toaster]:!border-amber-500/50",
          info: "group-[.toaster]:!border-primary/40",
          closeButton:
            "group-[.toast]:bg-background group-[.toast]:border-border group-[.toast]:text-foreground",
        },
      }}
      {...props}
    />
  );
};

export { Toaster, toast };
